import type { FsId } from '@fullstory/recording-api';
import { toNormalizedTagName } from '@fullstory/rewriters';
import {
  DOCUMENT_NODE_TAG,
  POLYFILL_SHADOW_NODE_TAG,
  SHADOW_NODE_TAG,
} from '@fullstory/shared/node_decoder';
import { DirtyBit } from './dirtybits';
import { FsCSSOMSheet } from './fscssom';
import type { FsCSSOMElement } from './fscssomelement';
import type { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';

/**
 * The vDOM representation of the {@link DocumentOrShadowRoot} mixin from the
 * HTML spec.
 */
export abstract class FsDocumentOrShadowRoot extends FsNode {
  protected _adoptedStyleSheets: FsCSSOMSheet[] = [];
  protected _adoptedStyleElsContainer: FsNode | undefined = undefined;

  setAdoptedStyleSheets(newSheetIds: number[]): void {
    // Ideally, we'd flush adopted stylesheets to
    // `DocumentOrDocumentFragment.prototype.adoptedStyleSheets` itself.
    // But currently, that API is only supported on Chrome. To support other
    // browsers, we'll emulate it by injecting style elements into our
    // virtual DOM.

    // The element to inject into.
    let layoutRootNode: FsNode | null = null;
    switch (this.tag) {
      case DOCUMENT_NODE_TAG:
        layoutRootNode = this.ownerDoc.body;
        break;
      case SHADOW_NODE_TAG:
        layoutRootNode = this;
        break;
    }

    if (!layoutRootNode) {
      throw new Error(`Couldn't set adopted stylesheets for <${this.tag}> node`);
    }

    const doc = this.ownerDoc;
    const oldSheets = this._adoptedStyleSheets;
    const newSheets = newSheetIds.map(id => doc.styleSheetById(id));
    this._adoptedStyleSheets = newSheets;

    // We need to create style nodes and associate them with sheets
    // by adding the nodes to the sheets' `ownerNodes` sets.
    //
    // But first, remove and disassociate the old style elements.
    if (this._adoptedStyleElsContainer) {
      this._adoptedStyleElsContainer.forEachChild((n: FsNode, i: number) => {
        // Note the assumption here: Style elements must be in the same order
        // as their corresponding sheets would be in adoptedStyleSheets.
        oldSheets[i].ownerNodes.delete(n as FsCSSOMElement);
      });
      this._adoptedStyleElsContainer.remove();
    }
    const container = (this._adoptedStyleElsContainer = doc.createHelperNode('div'));
    container.setAttr('id', 'adopted-style-container');
    container.setAttr('style', 'display: none;');

    for (const sheet of newSheets) {
      const style = doc.createHelperNode('style');
      style.linkShadowRoot(this);
      sheet.ownerNodes.add(style);
      container.appendChild(style);
    }

    // Put styles at the end to match adoptedStyleSheets' priority.
    layoutRootNode.ensureFooterChild().appendChild(container);

    this.setDirtyBit(DirtyBit.AdoptedStyleSheets);
  }

  public onSheetChange() {
    this.setDirtyBit(DirtyBit.AdoptedStyleSheets);
  }

  protected flushProperties(dom: Node): void {
    super.flushProperties(dom);

    if (this._hasDirtyBit(DirtyBit.AdoptedStyleSheets)) {
      this._flushAdoptedStyleSheets();
      this._clearDirtyBit(DirtyBit.AdoptedStyleSheets);
    }
  }

  protected invalidateProperties(): void {
    super.invalidateProperties();

    if (this._adoptedStyleSheets.length > 0) {
      this.setDirtyBit(DirtyBit.AdoptedStyleSheets);
    }
  }

  // This must be called *after* the DOM is flushed.
  private _flushAdoptedStyleSheets(): void {
    for (const sheet of this._adoptedStyleSheets) {
      sheet.flush();
    }
  }
}

/**
 * The vDOM representation of a {@link Document} node.
 *
 * Note that in the vDOM, {@link Document} is essentially split in two. The
 * {@link Node} aspects of it are handled by this class, while the other
 * document APIs are handled by {@link FsDocument}.
 */
export class FsDocumentNode extends FsDocumentOrShadowRoot {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, toNormalizedTagName(DOCUMENT_NODE_TAG), undefined, fsid);
  }

  /** @override */
  invalidateDomSubtree(): void {
    // Invalidating the document node is useless since it always uses the same
    // DOM node. Just invalidate descendants.
    this.forEachChild(child => child.invalidateDomSubtree());
  }

  protected createNode(doc: Document): Document {
    return doc;
  }
}

/**
 * A {@link ShadowRoot}-like node. This doesn't correspond to a concept from the
 * real DOM; it's here to abstract over the difference between real shadow roots
 * and polyfilled shadow roots.
 */
export abstract class FsShadowRootLike extends FsDocumentOrShadowRoot {
  attachShadowRootTo(parent: FsNode): void {
    this.parent = parent;
    this.setDirtyBit(DirtyBit.NewlyAttached);
    this.setAttached();
  }

  abstract flushShadowRoot(parentDom: HTMLElement): void;

  protected createNode(_doc: Document): null {
    // Shadow roots are created via flushShadowRoot(); unlike normal children,
    // they can't be created independently of their parent element.
    return null;
  }
}

/** The vDOM representation of a {@link ShadowRoot}. */
export class FsShadowRoot extends FsShadowRootLike {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, toNormalizedTagName(SHADOW_NODE_TAG), undefined, fsid);
  }

  flushShadowRoot(parentDom: HTMLElement): void {
    if (!parentDom.shadowRoot) {
      parentDom.attachShadow({ mode: 'open' });
    }
    this.dom = parentDom.shadowRoot;
  }
}

/**
 * The vDOM representation of a polyfilled {@link ShadowRoot}. It behaves just
 * like polyfilled shadow roots behave at recording time: we simulate a real
 * shadow root by treating the shadow DOM children as real children of the
 * shadow host.
 */
export class FsPolyfillShadowRoot extends FsShadowRootLike {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, toNormalizedTagName(POLYFILL_SHADOW_NODE_TAG), undefined, fsid);
  }

  flushShadowRoot(_parentDom: HTMLElement): void {
    // for polyfilled ShadowRoots, there's no actual shadow root to flush
    // instead, we flush the children of the shadow root to its parent
    this.dom = _parentDom;
  }
}
